import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { cx } from "shared/utils";
import {
  Button,
  ButtonSize,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Show
} from "shared/components";
import { Loader } from "shared/components/Loader";
import {
  ArrowLeft01Icon,
  ArrowRight01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { Tabs, type Tab } from "shared/components/BaseTabs";
import { getErrorMessage } from "utils/error";
import { startOfSunSatWeek } from "views/showings/helpers";
import {
  AVAILABLE_FILL_CLASS,
  BAND_END_HOUR,
  BAND_START_HOUR,
  SHOWING_FILL_CLASS,
  UNAVAILABLE_HATCH_STYLE
} from "./constants";
import { buildWeekDays, formatWeekLabel } from "./helpers";
import { useAvailabilityOverview } from "./useAvailabilityOverview";
import { DayTimeline } from "./DayTimeline";

interface ShowingAvailabilityTabProps {
  propertyId?: number;
  unitId?: number;
}

export const ShowingAvailabilityTab = ({
  propertyId,
  unitId
}: ShowingAvailabilityTabProps) => {
  const [weekOffset, setWeekOffset] = useState(0);
  // null = follow today's weekday; becomes a number once the user picks a chip.
  const [dayIndexOverride, setDayIndexOverride] = useState<number | null>(null);
  // "Now" is frozen at mount (and refreshed by the Today button) so an open
  // tab does not silently shift its week/day when midnight rolls over.
  const [referenceNow, setReferenceNow] = useState<DateTime>(() =>
    DateTime.now()
  );
  // The backend buckets days in the property timezone, so "today" must be
  // resolved in it; the browser zone only bootstraps the first request.
  const [timezone, setTimezone] = useState("");

  const nowInPropertyTz = timezone
    ? referenceNow.setZone(timezone)
    : referenceNow;
  const weekAnchor = startOfSunSatWeek(nowInPropertyTz).plus({
    weeks: weekOffset
  });
  const selectedDayIndex = dayIndexOverride ?? nowInPropertyTz.weekday % 7;

  const startDate = weekAnchor.toFormat("yyyy-MM-dd");
  const endDate = weekAnchor.plus({ days: 6 }).toFormat("yyyy-MM-dd");
  const selectedDate = weekAnchor
    .plus({ days: selectedDayIndex })
    .toFormat("yyyy-MM-dd");

  const weekDays = useMemo(
    () => buildWeekDays(DateTime.fromISO(startDate)),
    [startDate]
  );

  const { data, isLoading, isFetching, isPlaceholderData, isError, error } =
    useAvailabilityOverview({
      propertyId,
      unitId,
      startDate,
      endDate
    });

  useEffect(() => {
    if (data?.timezone) setTimezone(data.timezone);
  }, [data?.timezone]);

  // For rendering, read the timezone off the payload directly — the state
  // copy above lags one render behind (effects run after commit), which would
  // otherwise produce one frame of invalid luxon zones ("") and NaN geometry.
  const effectiveTimezone = data?.timezone ?? timezone;

  const goToToday = () => {
    setReferenceNow(DateTime.now());
    setWeekOffset(0);
    setDayIndexOverride(null);
  };

  const shiftWeek = (deltaWeeks: number) =>
    setWeekOffset((prev) => prev + deltaWeeks);

  const dayTabs: Tab<string>[] = weekDays.map((day) => ({
    value: day.date,
    label: `${day.weekdayLabel} ${day.dayNumber}`
  }));

  const teamMembers = data?.team_members ?? [];

  // While keepPreviousData shows the previous week's payload, the newly
  // selected date does not exist in it — render the same weekday from the
  // placeholder payload instead of fabricating an all-"unavailable" grid.
  const payloadDays = teamMembers[0]?.days ?? [];
  const displayedDate = payloadDays.some((day) => day.date === selectedDate)
    ? selectedDate
    : (payloadDays[selectedDayIndex]?.date ?? selectedDate);

  // Each date carries its own band, so read the one for the day on screen.
  const dayBand = data?.day_bands.find((band) => band.date === displayedDate);
  const bandStartHour = dayBand?.band_start_hour ?? BAND_START_HOUR;
  const bandEndHour = dayBand?.band_end_hour ?? BAND_END_HOUR;
  const hasBand = bandEndHour > bandStartHour;

  return (
    <div className="mt-4 overflow-hidden rounded-lg border border-alpha-100 bg-white">
      {/* Controls + legend */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4">
        <div className="flex items-center gap-2">
          <IconButton
            size={IconButtonSize.SM}
            variant={IconButtonVariant.BASE}
            HugeIcon={ArrowLeft01Icon}
            aria-label="Previous week"
            onClick={() => shiftWeek(-1)}
          />
          <IconButton
            size={IconButtonSize.SM}
            variant={IconButtonVariant.BASE}
            HugeIcon={ArrowRight01Icon}
            aria-label="Next week"
            onClick={() => shiftWeek(1)}
          />
          <Button size={ButtonSize.SM} onClick={goToToday}>
            Today
          </Button>
          <span className="ml-4 text-base font-medium text-content-1000">
            {formatWeekLabel(weekAnchor)}
          </span>
          <Show if={isFetching && !isLoading}>
            <Loader size={18} />
          </Show>
        </div>

        {/* Legend (design: hatched unavailable, green available, pink showing) */}
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-2 text-sm font-medium text-content-300">
            <span
              aria-hidden="true"
              className="h-5 w-5 rounded-full border border-alpha-100"
              style={UNAVAILABLE_HATCH_STYLE}
            />
            Unavailable
          </span>
          <span className="flex items-center gap-2 text-sm font-medium text-content-300">
            <span
              aria-hidden="true"
              className={cx("h-5 w-5 rounded-full", AVAILABLE_FILL_CLASS)}
            />
            Available
          </span>
          <span className="flex items-center gap-2 text-sm font-medium text-content-300">
            <span
              aria-hidden="true"
              className={cx(
                "h-5 w-5 rounded-full border border-alpha-100",
                SHOWING_FILL_CLASS
              )}
            />
            Showing
          </span>
        </div>
      </div>

      {/* Day chips */}
      <div className="px-6 pb-3">
        <Tabs
          size="md"
          tabStyle="ghost-pink"
          tabs={dayTabs}
          active={selectedDate}
          onChange={(date) => {
            const index = weekDays.findIndex((day) => day.date === date);
            setDayIndexOverride(index >= 0 ? index : null);
          }}
        />
      </div>

      {/* Content */}
      <Show if={isLoading}>
        <div className="flex justify-center border-t border-alpha-80 py-12">
          <Loader />
        </div>
      </Show>

      <Show if={!isLoading && isError}>
        <div className="border-t border-alpha-80 p-4 text-sm text-danger-500">
          {getErrorMessage(error, "Failed to load showing availability.")}
        </div>
      </Show>

      <Show if={!isLoading && !isError && teamMembers.length === 0}>
        <div className="border-t border-alpha-80 p-8 text-center text-sm text-content-400">
          No team members with configured availability for this property.
        </div>
      </Show>

      <Show if={!isLoading && !isError && teamMembers.length > 0 && !hasBand}>
        <div className="border-t border-alpha-80 p-8 text-center text-sm text-content-400">
          No availability or scheduled showings for this day.
        </div>
      </Show>

      <Show if={!isLoading && !isError && teamMembers.length > 0 && hasBand}>
        <div
          className={cx(
            "border-t border-alpha-80",
            isPlaceholderData && "opacity-60"
          )}
        >
          <DayTimeline
            teamMembers={teamMembers}
            selectedDate={displayedDate}
            timezone={effectiveTimezone}
            bandStartHour={bandStartHour}
            bandEndHour={bandEndHour}
          />
        </div>
      </Show>
    </div>
  );
};
